import { Box, Checkbox, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import { DesktopTimePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import weekday from 'dayjs/plugin/weekday';
import { Door, UpdateDoorOfficeModeInput } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import ArrayUtil from 'utils/Array/Array.util';
import DrawerTemplate from 'v2/components/Drawer/DrawerTemplate';
import useDrawerContext from 'v2/components/Drawer/useDrawerContext';
import Loader from 'v2/components/Loader/Loader';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import useDeviceDetails from 'v2/hooks/useDeviceDetails/useDeviceDetails';
import { getBrowser12HourTime } from 'v2/utils/dateTime';
import { isOfficeModeAvailable, isOfficeModeAvailableAndEnabled } from 'v2/utils/lock';

dayjs.extend(customParseFormat);
dayjs.extend(weekday);
interface FormValues {
  name: string;
  isOfficeModeEnabled: boolean;
  officeModeFrom: Dayjs | null;
  officeModeTo: Dayjs | null;
}

interface Props {
  device: Door;
}

export const FORM_ID = 'edit-device-drawer-form';

const EditDeviceDrawer = ({ device }: Props) => {
  const { translation } = useTranslation();
  const { closeDrawer } = useDrawerContext();
  const { showSnackbar } = useSnackbarContext();
  const [error, setError] = useState<string>();

  const { deviceLoading, updateDoorInfo, updateDoorInfoLoading } = useDeviceDetails({
    translation,
    handleFetchError: (msg) => showSnackbar(msg, { severity: 'error' }),
    deviceId: device.id
  });

  const {
    register,
    handleSubmit,
    watch,
    getValues,
    control,
    trigger,
    formState: { errors, isSubmitting }
  } = useForm<FormValues>({
    defaultValues: {
      name: device.name,
      isOfficeModeEnabled: isOfficeModeAvailableAndEnabled(device),
      officeModeFrom: isOfficeModeAvailableAndEnabled(device)
        ? dayjs(dayjs(`${dayjs().format('YYYY-MM-DD')} ${device.officeMode.weekDays[0].from}`, 'YYYY-MM-DD HH:mm'))
        : dayjs().set('hour', 7).set('minute', 0).set('second', 0),
      officeModeTo: isOfficeModeAvailableAndEnabled(device)
        ? dayjs(dayjs(`${dayjs().format('YYYY-MM-DD')} ${device.officeMode.weekDays[0].to}`, 'YYYY-MM-DD HH:mm'))
        : dayjs().set('hour', 20).set('minute', 0).set('second', 0)
    },
    mode: 'onTouched'
  });

  const isOfficeModeEnabled = watch('isOfficeModeEnabled');
  const fromValue = watch('officeModeFrom');
  const toValue = watch('officeModeTo');

  const validateFrom = (val: Dayjs | null) => {
    if (!getValues('isOfficeModeEnabled')) return true;

    const to = getValues('officeModeTo');
    if (!val || !to) return translation.time_range_invalid_error;
    return val.isBefore(to) || translation.time_range_invalid_error;
  };

  const validateTo = (val: Dayjs | null) => {
    if (!getValues('isOfficeModeEnabled')) return true;

    const from = getValues('officeModeFrom');
    if (!from || !val) return translation.time_range_invalid_error;
    return from.isBefore(val) || translation.time_range_invalid_error;
  };

  const onSubmit = async (data: FormValues) => {
    setError(undefined);
    if (!updateDoorInfoLoading) {
      const officeModeInput: UpdateDoorOfficeModeInput | undefined = isOfficeModeAvailable(device)
        ? {
            scheduleType: 'weekSchedule',
            weekSchedule:
              isOfficeModeEnabled && fromValue && toValue
                ? ArrayUtil.Range(7).map((_, index) => ({
                    dayName: dayjs().weekday(index).format('dddd').toLowerCase(),
                    from: fromValue.format('HH:mm:ss'),
                    to: toValue.format('HH:mm:ss')
                  }))
                : []
          }
        : undefined;
      const result = await updateDoorInfo(getValues('name'), device.id, officeModeInput);
      if (result.success) {
        showSnackbar(translation.saved_changes, { severity: 'success' });
        closeDrawer();
      } else {
        setError(result.errorMessage ?? translation.devices_error_update);
      }
    }
  };

  const isLoading = deviceLoading || device === undefined;

  return (
    <DrawerTemplate
      loading={isSubmitting}
      title={translation.edit_device}
      primaryAction={{
        label: translation.save,
        onClick: handleSubmit(onSubmit),
        loading: isLoading || isSubmitting,
        form: FORM_ID,
        id: 'save-button'
      }}
      secondaryAction={{ label: translation.cancel, id: 'cancel-button' }}
      error={error}
      onDismissError={() => setError(undefined)}
    >
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Loader />
        </Box>
      ) : (
        <Stack id={FORM_ID} component="form" onSubmit={handleSubmit(onSubmit)} noValidate gap={3} mt={1}>
          <TextField
            {...register('name', { required: translation.name_cannot_be_empty })}
            id="name-input"
            label={translation.name}
            variant="outlined"
            error={!!errors.name}
            helperText={errors.name?.message}
          />

          {isOfficeModeAvailable(device) && (
            <>
              <Stack>
                <Typography variant="subtitle1">{translation.office_mode}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {translation.office_mode_description}
                </Typography>
              </Stack>

              <Controller
                name="isOfficeModeEnabled"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="isOfficeModeEnabled-checkbox"
                        checked={!!field.value}
                        onChange={async (e) => {
                          field.onChange(e.target.checked);
                        }}
                        color="primary"
                      />
                    }
                    label={translation.allow_office_mode}
                    sx={{ m: 0 }}
                  />
                )}
              />

              <Stack direction="row" gap={1} alignItems="flex-start">
                <Controller
                  name="officeModeFrom"
                  control={control}
                  rules={{ validate: validateFrom }}
                  render={({ field }) => (
                    <DesktopTimePicker
                      ampm={getBrowser12HourTime()}
                      value={dayjs(field.value)}
                      disabled={!isOfficeModeEnabled}
                      onChange={async (val: Dayjs | null) => {
                        field.onChange(dayjs(val));
                        await trigger(['officeModeFrom', 'officeModeTo']);
                      }}
                      slotProps={{
                        textField: {
                          id: 'officeModeFrom-input',
                          size: 'small' as const,
                          error: !!errors.officeModeFrom,
                          helperText: errors.officeModeFrom?.message,
                          onBlur: field.onBlur,
                          sx: { flex: 1 }
                        }
                      }}
                    />
                  )}
                />

                <Typography variant="body1" color="textPrimary" sx={{ mt: 1 }}>
                  {translation.to}
                </Typography>

                <Controller
                  name="officeModeTo"
                  control={control}
                  rules={{ validate: validateTo }}
                  render={({ field }) => (
                    <DesktopTimePicker
                      ampm={getBrowser12HourTime()}
                      value={dayjs(field.value)}
                      disabled={!isOfficeModeEnabled}
                      onChange={async (val: Dayjs | null) => {
                        field.onChange(dayjs(val));
                        await trigger(['officeModeFrom', 'officeModeTo']);
                      }}
                      slotProps={{
                        textField: {
                          id: 'officeModeTo-input',
                          size: 'small' as const,
                          error: !!errors.officeModeTo,
                          helperText: errors.officeModeTo?.message,
                          onBlur: field.onBlur,
                          sx: { flex: 1 }
                        }
                      }}
                    />
                  )}
                />
              </Stack>
            </>
          )}
        </Stack>
      )}
    </DrawerTemplate>
  );
};

export default EditDeviceDrawer;
